import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, MessageCircle, Tag } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import UserHoverCard from "@/components/UserHoverCard";
import { useAuth } from "@/lib/auth-context";
import { useMyLikes } from "@/hooks/use-my-likes";
import { getThumbUrl, handleThumbError } from "@/lib/image-url";
import { kitPath } from "@/lib/kit-slug";

const CONDITION_PL: Record<string, string> = {
  "New with tags": "Nowa z metkami",
  "New without tags": "Nowa bez metek",
  Excellent: "Bardzo dobry",
  Good: "Dobry",
  Fair: "Przeciętny",
};

interface FeedCardProps {
  id: string;
  club: string;
  playerName?: string | null;
  season: string;
  brand: string;
  kitType: string;
  condition?: string | null;
  photoUrl?: string;
  likesCount: number;
  commentsCount: number;
  ownerName?: string;
  ownerAvatar?: string;
  ownerUserId: string;
  isLikedByMe: boolean;
  forSale?: boolean;
}

export default function FeedCard({
  id,
  club,
  playerName,
  season,
  brand,
  kitType,
  condition,
  photoUrl,
  likesCount: initialLikes,
  commentsCount,
  ownerName,
  ownerAvatar,
  ownerUserId,
  isLikedByMe: initialLiked,
  forSale = false,
}: FeedCardProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isLiked, setLike } = useMyLikes();
  const [likes, setLikes] = useState(initialLikes);
  const [busy, setBusy] = useState(false);
  const liked = isLiked(id, initialLiked);

  useEffect(() => {
    setLikes(initialLikes);
  }, [initialLikes]);

  const toggleLike = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      navigate("/auth");
      return;
    }
    if (busy) return;
    setBusy(true);

    if (liked) {
      setLike(id, false);
      setLikes((l) => Math.max(0, l - 1));
      await supabase.from("kit_likes").delete().eq("kit_id", id).eq("user_id", user.id);
    } else {
      setLike(id, true);
      setLikes((l) => l + 1);
      await supabase.from("kit_likes").insert({ kit_id: id, user_id: user.id });
    }
    setBusy(false);
  };

  return (
    <article className="overflow-hidden rounded-xl border bg-card shadow-sm">
      <UserHoverCard userId={ownerUserId} displayName={ownerName} avatarUrl={ownerAvatar}>
        <Link
          to={`/profile/${ownerUserId}`}
          className="flex flex-col items-center gap-1.5 px-4 py-3 text-center transition-colors hover:bg-muted/50"
        >
          <Avatar className="h-8 w-8">
            <AvatarImage src={ownerAvatar || undefined} />
            <AvatarFallback className="text-xs">
              {(ownerName || "?")[0].toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <span className="text-sm font-semibold text-foreground break-words">{ownerName || "Anonim"}</span>
        </Link>
      </UserHoverCard>

      {/* Photo */}
      <Link to={kitPath(id, club, season, kitType)} className="block">
        <div className="relative aspect-[4/3] overflow-hidden bg-muted">
          {photoUrl ? (
            <picture className="block h-full w-full">
              <source media="(min-width: 768px)" srcSet={photoUrl} />
              <img
                src={getThumbUrl(photoUrl)}
                onError={handleThumbError(photoUrl)}
                alt={`${club} ${season}`}
                loading="lazy"
                decoding="async"
                className="h-full w-full object-contain"
              />
            </picture>
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <Tag className="h-16 w-16 text-muted-foreground/20" />
            </div>
          )}
          {forSale && (
            <div className="absolute inset-x-0 bottom-0 z-10 bg-white/75 px-3 py-1.5 text-center text-[11px] font-bold uppercase tracking-wider text-pitch-dark backdrop-blur-sm sm:text-xs">
              Na sprzedaż
            </div>
          )}
        </div>
      </Link>

      {/* Actions & info */}
      <div className="px-4 py-3 space-y-2">
        <div className="flex items-center gap-4">
          <button onClick={toggleLike} className="flex items-center gap-1.5 text-sm transition-colors hover:text-red-500">
            <Heart className={`h-5 w-5 ${liked ? "fill-red-500 text-red-500" : "text-muted-foreground"}`} />
            <span className="font-medium">{likes}</span>
          </button>
          <Link to={kitPath(id, club, season, kitType)} className="flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground">
            <MessageCircle className="h-5 w-5" />
            <span className="font-medium">{commentsCount}</span>
          </Link>
        </div>

        <div>
          <h3 className="text-base font-bold text-foreground">{club}</h3>
          {playerName && (
            <p className="text-sm font-medium text-foreground/80">{playerName}</p>
          )}
          <p className="text-sm text-muted-foreground">
            {[season, brand, condition ? CONDITION_PL[condition] || condition : null].filter(Boolean).join(" · ")}
          </p>
        </div>

        <Link to={kitPath(id, club, season, kitType)}>
          <Button variant="outline" size="sm" className="w-full mt-1">
            Zobacz koszulkę
          </Button>
        </Link>
      </div>
    </article>
  );
}
